<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import CustomButton from "@/components/Button/Button.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import { getTripBudgetMoneyIn, getTripCurrentBalance, getTripExpenses, getTripRefunds } from "@/api";
import { BaseBudget } from "@/models/Budget";
import { MoneyIn } from "@/models/MoneyIn";
import { Expense } from "@/models/Expense";
import { Refund } from "@/models/Refund";
import AddMoneyInForm from "@/components/TripBudgetOverview/AddMoneyInForm.vue";
import AddExpenseForm from "@/components/TripBudgetOverview/AddExpenseForm.vue";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores/userStore";
const userStore = useUserStore();


const props = defineProps<{
  id: string;
}>();

const status = ref<'loading' | 'success' | 'error'>('loading');
const tripBudget = ref<BaseBudget | null>(null);
const moneyIn = ref<{ moneyIn: MoneyIn[] }>({ moneyIn: [] });
const expenses = ref<{ expenses: Expense[] }>({ expenses: [] });
const refunds = ref<{ refunds: Refund[] }>({ refunds: [] });

const { t } = useI18n(); // Use the i18n instance

const radialBarOptions = computed(() => ({
  chart: {
    id: 'budget-radial',
    label: 'test'
  },
  plotOptions: {
    radialBar: {
      hollow: {
        margin: 15,
        size: "75%",
      },
      track: {
        background: '#ffffff50',
        strokeWidth: '100%',
      },
      dataLabels: {
        showOn: "always",
        name: {
          offsetY: -43,
          show: true,
          color: "#fff",
          fontSize: "14px",
          fontWeight: 200,
        },
        value: {
          color: "#fff",
          fontSize: "30px",
          fontWeight: 550,
          formatter: () => `€ ${tripBudget.value.currentBalance},00`,
          show: true
        }
      }
    }
  },
  fill: {
    colors: ['#fff']
  },
  stroke: {
    lineCap: "round",
  },
  labels: [[t('tripBudgetOverview.currentBalanceForExpenses')]]
}))

const radialBarSeries = computed(() => tripBudget?.value ? [tripBudget.value.currentBalance / tripBudget.value.expectedBalance * 100] : [])

const pieOptions = computed(() => ({
  chart: {
    type: 'donut',
  },
  dataLabels: {
    enabled: false
  },
  plotOptions: {
    pie: {
      expandOnClick: false,
      donut: {
        size: '85%',
        labels: {
          show: true,
          total: {
            label: [t('tripBudgetOverview.currentBalanceForExpenses')],
            show: true,
            showAlways: true,
            fontSize: '12px',
            color: '#00000040',
            formatter: (w) => {
              return `€ ${w.globals.series.reduce((a, b) => a + b, 0)},00`
            }
          }
        }
      }
    }
  },
  legend: {
    show: false,
  },
  colors: ['#7fb0f9', '#dbc7fa', '#85d7ab', '#f8ff99'],
  labels: [t('tripBudgetOverview.groceries'), t('tripBudgetOverview.coffee'), t('tripBudgetOverview.drinks'), t('tripBudgetOverview.restaurant')],
}));

function reduceExpense(type: 'groceries' | 'coffee' | 'drinks' | 'restaurant') {
  return expenses.value.expenses.reduce((a, b) => b.type === type ? a + Math.floor(parseFloat(b.amount)) : a, 0)
}

const pieSeries = computed(() => [reduceExpense('groceries'), reduceExpense('coffee'), reduceExpense('drinks'), reduceExpense('restaurant')])

const euro = new Intl.NumberFormat('el-GR', {
  style: 'currency',
  currency: 'EUR',
});

const showAddModal = ref(false)
const modalType = ref<'expenses' | 'money'>('expenses')

function fetchMoneyIn() {
  getTripBudgetMoneyIn(props.id)
      .then((response) => {
        moneyIn.value = response.data;
      })
      .catch(() => {
        status.value = 'error';
      })
}

function fetchExpenses() {
  getTripExpenses(props.id)
      .then((response) => {
        expenses.value = response.data;
      })
      .catch(() => {
        status.value = 'error';
      })
}

onMounted(() => {
  getTripCurrentBalance(props.id)
      .then((response) => {
        tripBudget.value = response.data;
        status.value = 'success';
      })

  fetchMoneyIn();

  fetchExpenses();

  getTripRefunds(props.id)
      .then((response) => {
        refunds.value = response.data;
      })
      .catch(() => {
        status.value = 'error';
      });
})
</script>

<template>
  <div class="container flex flex-row gap-4" v-if="status === 'success'">
    <div class="flex flex-col gap-5 h-fit w-2/3">
      <div class="flex w-full flex-col gap-5">
        <div
            class="bg-primary-blue w-full h-[400px] rounded-11xl flex flex-col items-center justify-center gap-2.5 p-6"
            v-if="tripBudget">
          <div class="self-start text-white font-semibold responsive-body-large">{{ t('tripBudgetOverview.currentBalance') }}</div>
          <apexchart width="333" height="300" type="radialBar" :options="radialBarOptions" :series="radialBarSeries">
          </apexchart>
        </div>
        <div class="bg-background-light w-full h-[400px] rounded-11xl gap-2.5 p-6 max-h-[400px] overflow-y-scroll">
          <div class="flex justify-between items-start mb-6">
            <div>
              <div class="self-start font-semibold  responsive-body-medium mb-2">{{ t('tripBudgetOverview.moneyIn') }}</div>
              <div class="responsive-title-3 font-semibold">€ {{ tripBudget.currentBalance }},00</div>
            </div>
            <CustomButton type="black" :rounded="true" class="h-7"
                          @click="modalType = 'money'; showAddModal = true">{{ t('tripBudgetOverview.addMoneyIn') }}
            </CustomButton>
          </div>
          <div class="flex flex-col gap-4">
            <div v-for="addIn in moneyIn.moneyIn" :key="addIn.transactionId">
              <div class="flex flex-row items-center justify-between">
                <div class="flex flex-row items-center gap-2">
                  <div class="w-8 h-8 border border-black/50 rounded-full" />
                  <div>
                    <div class=" responsive-body-small font-semibold">{{ addIn.crewMember }}</div>
                    <div class="responsive-body-xsmall text-black/50">{{ userStore.formatDate(new Date(addIn.date)) }}</div>
                  </div>
                </div>
                <div>+€ {{ addIn.amount }},00</div>
              </div>
              <div class="ml-10 responsive-body-xsmall text-black/50 w-[230px]">
                {{ addIn.notes }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-background-light w-1/3 h-fit rounded-11xl gap-2.5 p-6 max-h-fit overflow-y-scroll">
      <div class="flex flex-row justify-between items-center mb-7">
        <div class="font-semibold responsive-body-medium">{{ t('tripBudgetOverview.expenses') }}</div>
        <CustomButton type="black" :rounded="true" class="h-7"
                      @click="modalType = 'expenses'; showAddModal = true">{{ t('tripBudgetOverview.addExpenses') }}
        </CustomButton>
      </div>
      <apexchart type="donut" :series="pieSeries" :options="pieOptions" v-if="pieSeries.some(a => a !== 0)"></apexchart>
      <div class="mt-7 flex flex-col gap-4">
        <div v-for="expense in expenses.expenses" :key="expense.uuid">
          <div class="flex flex-row items-center justify-between">
            <div class="flex flex-row items-center gap-2">
              <div class="w-8 h-8 border border-black/50 rounded-full" />
              <div>
                <div class=" responsive-body-small font-semibold">{{ expense.crewMember }}</div>
                <div class="responsive-body-xsmall text-black/50">{{ userStore.formatDate(new Date(expense.date)) }}</div>
              </div>
            </div>
            <div>+€ {{ expense.amount }},00</div>
          </div>
          <div v-if="expense.notes" class="ml-10 responsive-body-xsmall text-black/50 w-[230px]">
            {{ expense.notes }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <CustomModal :show="showAddModal">
    <div class="w-[465px] p-12">
      <div class="flex flex-col">
        <div class="cursor-pointer self-end" type="default" @click="showAddModal = false">
          <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18" />
        </div>
        <div class="responsive-title-5 font-semibold">{{ modalType === 'money' ? t('tripBudgetOverview.addMoneyIn') :
            t('tripBudgetOverview.addExpenses') }}</div>
      </div>
      <div class="mt-8">
        <AddMoneyInForm :trip-id="props.id" v-if="modalType === 'money'"
                        @on-completed="fetchMoneyIn(); showAddModal = false;" />
        <AddExpenseForm trip-id="props.id" v-else @on-completed="fetchExpenses(); showAddModal = false;" />
      </div>
    </div>
  </CustomModal>
</template>

<style scoped>
@media (max-width: 786px) {
  .desktop {
    display: none;
  }
}
</style>
